import React, { useEffect, useState, useRef, useMemo } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { Box, Divider, useMediaQuery } from '@mui/material';
import ReactStickyBox from 'react-sticky-box';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import '../../Styles/donateNow.css';
import '../../scss/fundraiser-list.scss';
import useForm from '../../hooks/UseForm';
import { deviceDetect } from 'react-device-detect';
import { validateCheckout } from '../../validations';
import { scrollToTop } from '../../helpers';
import Tracking from '../../services/tracking.service';
import { GTM_EVENTS, S3_BUCKET } from '../../constants';
import { RECAPTCHA_SCRIPT_URL, WEB_URL } from '../../api/URLs';
import { useNonInitialEffect } from '../../hooks/UseNonInitialEffect';
import { DonationImpactCard, Footer, Header, MixPanelTrackedLink } from '../../components';
import { StyledCheckoutForm } from './checkoutComponents/CheckoutStyles';
import { HBLForm } from './checkoutComponents/paymentOptionComponents//HBL';
import { SET_ACH_REF, SET_GOOGLE_APPLE_REF, SET_STRIPE_REF } from '../../store/actionTypes';
import { checkoutActions, donationActions, fundRaiserPageAction, userActions } from '../../store/actions';
import { GiftAid, CorporateMatch, ContactDetailsSection, PaymentsSection, AmountSection, DonateNowButton, KeepInTouch } from './checkoutComponents';
import { useNavigate } from 'react-router-dom';

export default function ({ isFundraiserCheckout = true, pageDetails }) {
	const ACHReference = useRef();
	const stripeReference = useRef();
	const googleApplePayReference = useRef();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const errors = useSelector((state) => state.validationErrors);
	const authentication = useSelector((state) => state.authentication);
	const partnerLogo = useSelector((state) => state.donation.partnerLogo);
	const userCurrentLocation = useSelector((state) => state.fundraiser_page.location_details);
	const taxesMessage = useSelector((state) => state.donation.checkoutMessages.taxes_message);
	const countryHasGiftAid = useSelector((state) => state.check_if_country_has_giftaid.if_giftaid);
	const countryAllowedForCorporateMatch = useSelector((state) => state.isCountryCorporateMatch.if_corporate_match);
	const checkoutState = useSelector((state) => state.donationCheckout);
	const isDesktop = useMediaQuery('(min-width:992px)');
	const [startTime, setStartTime] = useState(null);

	const handleStateChange = (event) => {
		dispatch(checkoutActions.updateCheckoutState(event));
	};

	const processDonation = async (googleApplePaymentId = '') => {
		window.grecaptcha.ready(async function () {
			dispatch(checkoutActions.setGoogleApplePayId(googleApplePaymentId));

			dispatch(checkoutActions.setPaymentRef(SET_ACH_REF, ACHReference));
			dispatch(checkoutActions.setPaymentRef(SET_STRIPE_REF, stripeReference));
			dispatch(checkoutActions.setPaymentRef(SET_GOOGLE_APPLE_REF, googleApplePayReference));

			dispatch(checkoutActions.initiateDonationProcess(startTime));
		});
	};
	const { handleChange, handleSubmit } = useForm(processDonation, validateCheckout, handleStateChange, checkoutState);

	useEffect(() => {
		setStartTime(new Date());
		scrollToTop();
		dispatch(fundRaiserPageAction.getUserDefaultCurrency());
		if (authentication.user) dispatch(donationActions.getUserDetails(authentication.user.id));
	}, []);

	useNonInitialEffect(() => {
		// Verifying if user's current location country is active by admin panel
		dispatch(donationActions.checkIfCountryPartnerIsactiveDonor(userCurrentLocation.country_id));
	}, [userCurrentLocation]);

	useNonInitialEffect(() => {
		if (!isFundraiserCheckout && !pageDetails.allow_direct_donations) {
			navigate(WEB_URL.PAGE_NOT_FOUND);
		} else
			Tracking.trackGTM({
				event: GTM_EVENTS.PAYMENT_INITIATED,
				fundrasier_name: pageDetails.page_title,
				raised_amount: pageDetails.total_fund_raised,
				number_of_donation: pageDetails.count_donations,
				goal_amount: pageDetails.fundraising_goal,
			});

		dispatch(donationActions.checkIfCountryPartnerIsactiveFundraiser(pageDetails.country_id));
	}, [pageDetails]);

	useEffect(() => {
		if (checkoutState.userContactDetails.country && checkoutState.selectedCountry.id)
			dispatch(userActions.checkAllowedCountryForCheque(checkoutState.userContactDetails.country, checkoutState.selectedCountry.id));
	}, [checkoutState.selectedCountry.id, checkoutState.userContactDetails.country]);

	useEffect(() => {
		dispatch(
			checkoutActions.updateCheckoutState({
				target: {
					name: 'isFundraiserCheckout',
					value: isFundraiserCheckout,
				},
			}),
		);
	}, [isFundraiserCheckout]);

	useEffect(() => {
		const script = document.createElement('script');

		script.src = RECAPTCHA_SCRIPT_URL;
		script.async = true;

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	return (
		<React.Fragment>
			<Header />
			<StyledCheckoutForm className="App-content">
				<Grid className="OuterGrid" container spacing={isDesktop ? 2 : 0}>
					<Grid item sm={12} md={isDesktop ? 8 : 12}>
						<Stack>
							{pageDetails && !isDesktop ? (
								<>
									<DonationImpactCard pageDetails={pageDetails} assignClasses="whitebox" isFundraiserPage={isFundraiserCheckout} />
									<Divider className="section-divider" />
								</>
							) : null}
							{pageDetails && (
								<>
									<AmountSection
										isFundraiserCheckout={isFundraiserCheckout}
										handleChange={handleChange}
										validationErrors={errors}
										pageDetails={pageDetails}
									/>
									{!isDesktop && <Divider className="section-divider" />}
								</>
							)}
							{/* Donation impact card will display before user contact details on mobile screen */}
							<Box sx={{ marginBottom: '20px' }}>
								<ContactDetailsSection handleChange={handleChange} validationErrors={errors} pageDetails={pageDetails} />
								{countryAllowedForCorporateMatch && <CorporateMatch validationErrors={errors} handleChange={handleChange} />}
							</Box>
							{!isDesktop && <Divider className="section-divider" />}
							<Box sx={{ marginBottom: '20px' }}>
								<PaymentsSection
									ACHReference={ACHReference}
									stripeReference={stripeReference}
									handleChange={handleChange}
									validationErrors={errors}
								/>
								{countryHasGiftAid && <GiftAid handleChange={handleChange} validationErrors={errors.giftAidBilling} />}
							</Box>
							{!isDesktop && <Divider className="section-divider" />}
							<KeepInTouch handleCheck={handleChange} />
							<DonateNowButton googleApplePayReference={googleApplePayReference} handleClick={handleSubmit} />
							{partnerLogo ? <img style={{ width: '150px' }} src={S3_BUCKET + partnerLogo} /> : null}
							{taxesMessage ? <Typography variant="small">{taxesMessage}</Typography> : null}
							<Typography align="center" variant="small" color="neutral.dark" sx={{ marginTop: '8px' }}>
								<i>
									This site is protected by reCAPTCHA and the Google &nbsp;
									<MixPanelTrackedLink
										type="link"
										label="Privacy Policy"
										context="Google recaptcha privacy policy"
										href="https://policies.google.com/privacy"
										color="neutral.dark"
									>
										<i>Privacy Policy</i>
									</MixPanelTrackedLink>{' '}
									and &nbsp;
									<MixPanelTrackedLink
										type="link"
										label="Terms of Service apply."
										context="Google recaptcha Terms of Service apply."
										href="https://policies.google.com/terms"
										color="neutral.dark"
									>
										<i>Terms of Service apply.</i>
									</MixPanelTrackedLink>
								</i>
							</Typography>
						</Stack>
					</Grid>
					<Grid item xs={12} md={4} className="dright">
						<ReactStickyBox offsetTop={20}>
							{pageDetails && isDesktop ? (
								<DonationImpactCard pageDetails={pageDetails} assignClasses="whitebox" isFundraiserPage={isFundraiserCheckout} />
							) : null}
						</ReactStickyBox>
					</Grid>
					<HBLForm />
				</Grid>
			</StyledCheckoutForm>
			<Footer />
		</React.Fragment>
	);
}
